import React, { useEffect, useRef, useState } from "react";
import { ImageBuilder } from "../../global/image-builder";
import { cn } from "../../../lib/helpers";
import tailwindConfig from "../../../../tailwind.config";
import { QuizResults } from "./quiz-results";

// Jumbo text section with scroll effects
export const QuizProcess = ({ section }) => {
  const containerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate scroll progress when section is in view
      const sectionTop = rect.top;
      const sectionHeight = rect.height;

      // Progress from 0 to 1 as section scrolls through viewport
      const progress = Math.max(0, Math.min(1,
        (windowHeight - sectionTop) / (windowHeight + sectionHeight)
      ));

      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate transform based on scroll progress
  // Cards start at bottom (90%) and move to top (30%) as user scrolls
  const cardTransform = `translateY(${60 - (scrollProgress * 60)}%)`;

  // Calculate opacity for before/after images with offset - smooth crossfade
  // Add offset so fade starts when cards are more visible (0.2 to 0.8 of scroll progress)
  const fadeStart = 0.43;
  const fadeEnd = 0.65;
  const fadeProgress = Math.max(0, Math.min(1, (scrollProgress - fadeStart) / (fadeEnd - fadeStart)));

  const beforeOpacity = 1 - fadeProgress; // Fade out during fade range
  const afterOpacity = fadeProgress; // Fade in during fade range

  return (
    <div
      ref={containerRef}
      className="relative space-y-1 uppercase italic font-superhero flex items-start justify-center"
      style={{ paddingBottom: "16rem" }}
    >
      {/* Three Superpowers Text */}
      <div
        className="relative z-10"
        style={{
          fontSize: "clamp(4.5rem, 8vw, 7rem)",
          lineHeight: "clamp(5rem, 10vw, 8.5rem)",
          fontWeight: "300",
        }}
      >
        <h2 className="text-white">
          Three <span className="font-normal">Superpowers.</span>
        </h2>
        <h3 className="text-light-60">
          One <span className="font-normal">Mission:</span>
        </h3>
        <h3 className="opacity-30 tracking-tight"
          style={{ letterSpacing: "0" }}
        >
          Maximum <span className="font-normal">Relevance.</span>
        </h3>
      </div>

      {/* Example Hero Cards - Positioned Absolutely with Scroll Effect */}
      <div
        className="absolute inset-0 z-20"
        style={{
          transform: cardTransform,
          transition: 'transform 0.1s ease-out'
        }}
      >
        <div className="flex justify-between w-full gap-8 sm:gap-24"
          style={{ minHeight: "30rem" }}>
          <HeroCard
            cardNumber={1}
            beforeOpacity={beforeOpacity}
            afterOpacity={afterOpacity}
            className="flex-grow"
            positionClass="self-center"
            color={tailwindConfig.theme.colors.superhero.striker}
          />
          <HeroCard
            cardNumber={3}
            beforeOpacity={beforeOpacity}
            afterOpacity={afterOpacity}
            className="mr-4 sm:ml-36"
            positionClass="self-end"
            color={tailwindConfig.theme.colors.superhero.resonator}
          />
          <HeroCard
            cardNumber={2}
            beforeOpacity={beforeOpacity}
            afterOpacity={afterOpacity}
            positionClass="self-start"
            color={tailwindConfig.theme.colors.superhero.oracle}
          />
        </div>
      </div>
    </div>
  );
};

// Hero Card Component with fade transition and glow effect
const HeroCard = ({ cardNumber, beforeOpacity, afterOpacity, positionClass, className, color }) => {

  return (
    <div className={cn("relative", positionClass, className)}>
      {/* Card container with shadow glow */}
      <div
        className="relative inline-block"
        style={{
          boxShadow: `0 0 60px ${color}80`,
          filter: `drop-shadow(0 0 30px ${color}40)`,
          borderRadius: '12px'
        }}
      >
        {/* Card images */}
        <div className="relative">
          <ImageBuilder
            src={`/img/quiz/example-${cardNumber}-hero-before.png`}
            alt="example hero portrait before"
            className="rounded-xl"
            width={146}
            style={{ opacity: beforeOpacity }}
          />
          <ImageBuilder
            src={`/img/quiz/example-${cardNumber}-hero-after.png`}
            alt="example hero portrait after"
            className="rounded-xl absolute inset-0"
            width={146}
            style={{ opacity: afterOpacity }}
          />
        </div>
      </div>
    </div>
  );
};

export const QuizSectionHeader = ({ bgTitle, title, summary }) => (
  <div className="relative">
    <h4 className="absolute -top-16 md:-top-20 lg:-top-32 max:-top-42 left-1/2 transform -translate-x-1/2 uppercase italic opacity-10 font-superheroBold text-white whitespace-nowrap select-none"
      style={{
        fontSize: "clamp(5.5rem, 14vw, 11rem)",
        fontWeight: "900",
      }}
    >
      {bgTitle}
    </h4>

    <div className="text-center mb-8">
      <h2 className="text-white font-superhero font-semibold text-3xl md:text-6xl">
        {title}
      </h2>
    </div>

    <div className="text-center mb-12 max-w-xl mx-auto">
      <div className="text-lg leading-normal text-light-60 max-w-2xl mx-auto font-openSans">
        {summary}
      </div>
    </div>
  </div>
);

export const QuizTest = () => {
  const [quizResult, setQuizResult] = useState({
    email: "test@example.com",
    dept: "Marketing",
    persona: "Marketing",
    role: "VP",
    seniority: "VP",
    archetype: "Oracle",
    cta: "Unlock your team's superpowers with AI-powered customer intelligence",
    matches: [
      { name: "Oracle", score: "8/9" },
    ],
    org: {
      facts: {
        name: "Example Corp",
        employees: "500-1000",
        traffic: "100K-500K visits/month",
        teamSize: "15-25",
        social: "10K+ followers",
        funding: "Series B",
        offering: "B2B SaaS Platform",
        industry: "Marketing Technology"
      },
      background: {
        company: "Example Corp is a leading marketing technology company focused on helping B2B companies understand their customers better.",
        employees: "The company has grown rapidly to 750 employees across 5 offices globally.",
        traffic: "Website receives approximately 250K monthly visitors with strong organic growth.",
        teamSize: "Marketing team of 20 professionals handling demand gen, content, and product marketing.",
        social: "Strong social media presence with 15K LinkedIn followers and active community engagement.",
        funding: "Recently closed $50M Series B led by prominent VC firms.",
        offering: "Provides a comprehensive customer intelligence platform that integrates data from multiple sources.",
        industry: "Operates in the rapidly growing martech space with focus on B2B segment."
      }
    }
    // Note: No content yet - this will trigger the content loading behavior
  });

  useEffect(() => {
    // Simulate content loading after 9 seconds (to test polling UI)
    const contentTimer = setTimeout(() => {
      setQuizResult(prev => ({
        ...prev,
        content: {
          archetypeResults: "You are the Oracle, a visionary leader who sees patterns others miss.\nYour strategic insights drive long-term success and help your team navigate complex challenges with clarity and confidence.",
          strengthGrowth: "Your ability to analyze data and identify trends is exceptional. You excel at connecting disparate pieces of information to form a cohesive strategy.\nGrowth opportunity: Consider balancing your analytical approach with more rapid experimentation to test hypotheses faster.",
          pitfalls: "Watch out for analysis paralysis - sometimes perfect information isn't available and quick decisions are needed.\nAvoid getting too caught up in long-term planning at the expense of addressing immediate tactical needs.",
          nextFocus: "Focus on building systems that help your team act on insights more quickly.\nPrioritize creating feedback loops that validate your strategic hypotheses in real-time.\nConsider how you can empower others to make data-driven decisions without always needing your input.",
          share: "I'm an Oracle who sees the patterns others miss! My superpower is turning data into strategic insights that drive real results."
        }
      }));
    }, 9000);

    return () => clearTimeout(contentTimer);
  }, []);

  return <QuizResults
    quizResult={quizResult}
    sessionHash="test-session-123"
  />;
};